import { useEffect } from 'react';

/**
 * Production site URL. Update this if you bind a custom domain.
 * Client-side canonical/OG URLs fall back to the live origin at runtime,
 * so this constant is mainly used for absolute references in structured data.
 */
export const SITE_URL = 'https://www.globallinkgrouphk.com';

export const ORG_NAME_TC = '匯通環球策略有限公司';
export const ORG_NAME_EN = 'Global Link Strategy Limited';

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

interface SeoOptions {
  title: string;
  description: string;
  /** Path only, e.g. "/services". Canonical is built from the live origin. */
  path?: string;
  keywords?: string;
  image?: string;
  /** One or more JSON-LD structured data objects for this page. */
  jsonLd?: JsonLd;
  ogType?: 'website' | 'article' | 'profile';
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

const MANAGED_JSONLD_ID = 'seo-page-jsonld';

/**
 * Lightweight, dependency-free SEO hook.
 * Updates <title>, meta tags, canonical link and injects page-level JSON-LD.
 * Cleans up the page-level JSON-LD on unmount to avoid stale structured data.
 */
export function useSeo(options: SeoOptions) {
  const { title, description, path, keywords, image, jsonLd, ogType = 'website' } = options;

  useEffect(() => {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : SITE_URL;
    const canonical = origin + (path ?? (typeof window !== 'undefined' ? window.location.pathname : '/'));
    const ogImage = image ?? `${origin}/favicon.png`;

    document.title = title;
    setMeta('name', 'title', title);
    setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);

    setCanonical(canonical);

    // Open Graph
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', ogImage);

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);

    // Page-level JSON-LD
    const prev = document.getElementById(MANAGED_JSONLD_ID);
    if (prev) prev.remove();
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = MANAGED_JSONLD_ID;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const node = document.getElementById(MANAGED_JSONLD_ID);
      if (node) node.remove();
    };
  }, [title, description, path, keywords, image, ogType, JSON.stringify(jsonLd)]);
}

/** Build a breadcrumb JSON-LD for a page. */
export function breadcrumb(items: { name: string; path: string }[], origin?: string) {
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : SITE_URL);
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: base + it.path,
    })),
  };
}
